import * as React from "react";
import { useState, useRef } from "react";
import { Box, Fade, IconButton, MenuItem, Portal, Select, Slider, styled, Tooltip } from "@mui/material";
import { lighten } from "@mui/system";
import Draggable, { DraggableEventHandler } from "react-draggable";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { conferenceLocalStorage } from "conference/tools";
import { CloseIcon } from "conference/components/icons";
import { WindowPanelSettings } from "conference/types";

const WindowPanelContainer = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    backgroundColor: lighten(theme.palette.background.default, 0.2),
    color: theme.palette.text.primary,
    transformOrigin: "top center",
    backdropFilter: "blur(5px)",
}));

const Wrapper = styled(Box)({
    position: "fixed",
});

const Caption = styled(Box)({
    fontSize: "1.5em",
    cursor: "move",
});

const Header = styled(Box)(({ theme }) => ({
    padding: "1em",
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    "&:hover .controls": {
        opacity: 1,
    },
}));

const Controls = styled(Box)({
    opacity: 0,
    transition: "opacity 0.2s",
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
});

const Content = styled(Box)({
    padding: "1em",
});

type Props = {
    show: boolean;
    children: React.ReactNode;
    title: MessageDescriptor;
    close?: () => void;
};

const scaleValues = [0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4, 1.5];

const WindowPanel = ({ show, children, title, close }: Props) => {
    const [position, setPosition] = useState(conferenceLocalStorage.windowPanelSettings[title.id]?.position);
    const [opacity, setOpacity] = useState(conferenceLocalStorage.windowPanelSettings[title.id]?.opacity ?? 1);
    const [scale, setScale] = useState(conferenceLocalStorage.windowPanelSettings[title.id]?.scale ?? 1);
    const nodeRef = useRef<HTMLElement>({} as any);

    function saveSetting<T extends keyof WindowPanelSettings>(key: T, value: WindowPanelSettings[T]) {
        conferenceLocalStorage.windowPanelSettings = {
            ...conferenceLocalStorage.windowPanelSettings,
            [title.id]: {
                ...conferenceLocalStorage.windowPanelSettings[title.id],
                [key]: value,
            },
        };
    }

    const onStop: DraggableEventHandler = (ev, data) => {
        saveSetting("position", { x: data.x, y: data.y });
        setPosition({ x: data.x, y: data.y });
    };

    return (
        <Portal container={document.body}>
            <Draggable handle=".caption" position={position} onStop={onStop} nodeRef={nodeRef}>
                <Wrapper ref={nodeRef}>
                    <Fade in={show} mountOnEnter unmountOnExit>
                        <WindowPanelContainer sx={{ opacity, transform: `scale(${scale}, ${scale})` }}>
                            <Header>
                                <Caption>
                                    <FormattedMessage {...title} />
                                </Caption>
                                <Controls className="controls">
                                    <Tooltip title={`Opacity: ${Math.round(opacity * 100)}%`} placement="top">
                                        <Slider
                                            min={5}
                                            max={100}
                                            value={opacity * 100}
                                            onChange={(event, value: number) => setOpacity(value / 100)}
                                            onChangeCommitted={(event, value: number) =>
                                                saveSetting("opacity", value / 100)
                                            }
                                            style={{ width: 100, marginLeft: "1em" }}
                                        />
                                    </Tooltip>
                                    <Select
                                        margin="dense"
                                        style={{ marginLeft: "1em" }}
                                        value={scale}
                                        onChange={(event) => {
                                            const val = event.target.value;
                                            setScale(val);
                                            saveSetting("scale", val);
                                        }}
                                    >
                                        {scaleValues.map((v) => (
                                            <MenuItem key={v} value={v}>
                                                {(v * 100).toFixed()}%
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Controls>
                                {Boolean(close) && (
                                    <IconButton onClick={close} size="small">
                                        <CloseIcon style={{ minWidth: 26 }} />
                                    </IconButton>
                                )}
                            </Header>
                            <Content>{children}</Content>
                        </WindowPanelContainer>
                    </Fade>
                </Wrapper>
            </Draggable>
        </Portal>
    );
};

export default WindowPanel;
